import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Wallet, Loader2, Repeat, Calendar as CalendarIcon, ArrowRight, ArrowLeft, ChevronsRight, X } from 'lucide-react';
import { format, parseISO, startOfDay, endOfDay } from 'date-fns';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Categories grouped by flow direction
const INFLOW_CATEGORIES = ["Loan drawdown", "Owner injection", "Other inflow"];
const OUTFLOW_CATEGORIES = ["Loan repayment", "Owner drawings", "Tax/GST", "Interest", "Capex", "Insurance", "Rent", "Wages", "Utilities", "Marketing", "Other outflow"];
const CASH_CATEGORIES = [...INFLOW_CATEGORIES, ...OUTFLOW_CATEGORIES];

const isOutflowCategory = (category) => OUTFLOW_CATEGORIES.includes(category);

export default function OtherCashPage() {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteAllStep, setDeleteAllStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    cash_date: format(new Date(), 'yyyy-MM-dd'),
    category: 'Other',
    counterparty: '',
    description: '',
    amount: '',
    is_recurring: false,
    recurrence_frequency: 'monthly',
    recurrence_mode: 'forever', // 'forever' | 'until'
    recurrence_end_date: '',
  });
  const [saving, setSaving] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  // Shift dialog state
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftItem, setShiftItem] = useState(null);
  const [shiftDays, setShiftDays] = useState(7);
  const [shiftDirection, setShiftDirection] = useState('later'); // 'later' | 'earlier'
  const [shiftScope, setShiftScope] = useState('this'); // 'this' | 'future'
  const [shifting, setShifting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/other-cash`);
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        cash_date: item.cash_date,
        category: item.category,
        counterparty: item.counterparty || '',
        description: item.description || '',
        amount: Math.abs(item.amount).toString(),
        is_recurring: item.is_recurring || false,
        recurrence_frequency: item.recurrence_frequency || 'monthly',
        recurrence_mode: 'forever',
        recurrence_end_date: '',
      });
    } else {
      setEditingItem(null);
      setFormData({
        cash_date: format(new Date(), 'yyyy-MM-dd'),
        category: 'Other outflow',
        counterparty: '',
        description: '',
        amount: '',
        is_recurring: false,
        recurrence_frequency: 'monthly',
        recurrence_mode: 'forever',
        recurrence_end_date: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.amount || formData.amount === '0') {
      toast.error('Please enter a valid amount');
      return;
    }

    // Validate recurrence end date if user opted to set one
    if (
      formData.is_recurring
      && formData.recurrence_mode === 'until'
      && !editingItem
    ) {
      if (!formData.recurrence_end_date) {
        toast.error('Please pick a recurrence end date');
        return;
      }
      if (formData.recurrence_end_date < formData.cash_date) {
        toast.error('End date must be on or after the start date');
        return;
      }
    }

    setSaving(true);
    try {
      // Automatically make amount negative for outflow categories
      let finalAmount = Math.abs(parseFloat(formData.amount));
      if (isOutflowCategory(formData.category)) {
        finalAmount = -finalAmount;
      }

      const payload = {
        ...formData,
        amount: finalAmount,
        recurrence_frequency: formData.is_recurring ? formData.recurrence_frequency : null,
        recurrence_end_date:
          formData.is_recurring && formData.recurrence_mode === 'until'
            ? formData.recurrence_end_date
            : null,
      };
      // Strip UI-only flags before sending
      delete payload.recurrence_mode;

      if (editingItem) {
        // Don't allow changing recurring status on edit
        delete payload.is_recurring;
        delete payload.recurrence_frequency;
        delete payload.recurrence_end_date;
        await axios.put(`${API}/other-cash/${editingItem.id}`, payload);
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/other-cash`, payload);
        const freqLabel = formData.is_recurring
          ? ` (${formData.recurrence_frequency})`
          : '';
        toast.success(`Entry created${freqLabel}`);
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/other-cash/${id}`);
      toast.success('Entry deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const openShiftDialog = (item) => {
    setShiftItem(item);
    // Default direction: outflows naturally get pushed later (delay), inflows pulled earlier
    const isOutflow = (item.amount || 0) < 0 || isOutflowCategory(item.category);
    setShiftDirection(isOutflow ? 'later' : 'earlier');
    setShiftDays(7);
    setShiftScope('this');
    setShiftDialogOpen(true);
  };

  const handleShift = async () => {
    if (!shiftItem) return;
    const days = Math.abs(parseInt(shiftDays, 10) || 0);
    if (days === 0) {
      toast.error('Please enter a non-zero number of days');
      return;
    }
    setShifting(true);
    try {
      const signedDays = shiftDirection === 'later' ? days : -days;
      const res = await axios.post(`${API}/other-cash/${shiftItem.id}/shift`, {
        days: signedDays,
        scope: shiftScope,
      });
      const verb = shiftDirection === 'later' ? 'pushed later' : 'brought forward';
      toast.success(`${res.data.updated} entr${res.data.updated === 1 ? 'y' : 'ies'} ${verb} by ${days} day${days === 1 ? '' : 's'}`);
      setShiftDialogOpen(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to shift entry');
    } finally {
      setShifting(false);
    }
  };

  const formatCurrency = (value) => {
    const formatted = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0
    }).format(Math.abs(value));
    return value < 0 ? `-${formatted}` : formatted;
  };

  const getCashClass = (value) => {
    if (value > 0) return 'cash-positive';
    if (value < 0) return 'cash-negative';
    return '';
  };

  // ---------- Filtering & Selection ----------
  const filteredItems = items.filter((it) => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matches = (it.category || '').toLowerCase().includes(q)
        || (it.counterparty || '').toLowerCase().includes(q)
        || (it.description || '').toLowerCase().includes(q)
        || String(it.amount || '').includes(q);
      if (!matches) return false;
    }
    if (dateFrom || dateTo) {
      if (!it.cash_date) return false;
      const d = parseISO(it.cash_date);
      if (dateFrom && d < startOfDay(parseISO(dateFrom))) return false;
      if (dateTo && d > endOfDay(parseISO(dateTo))) return false;
    }
    return true;
  });

  const filteredTotal = filteredItems.reduce((sum, it) => sum + (it.amount || 0), 0);
  const allFilteredSelected = filteredItems.length > 0
    && filteredItems.every((it) => selectedIds.has(it.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredItems.forEach((it) => next.delete(it.id));
      } else {
        filteredItems.forEach((it) => next.add(it.id));
      }
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearDateRange = () => {
    setDateFrom('');
    setDateTo('');
  };

  const handleBulkDeleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/other-cash/bulk-delete`, { ids });
      toast.success(`${res.data.deleted} entr${res.data.deleted === 1 ? 'y' : 'ies'} deleted`);
      setSelectedIds(new Set());
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entries');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleDeleteAllFiltered = async () => {
    const ids = filteredItems.map((it) => it.id);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      const res = await axios.post(`${API}/other-cash/bulk-delete`, { ids });
      toast.success(`${res.data.deleted} entr${res.data.deleted === 1 ? 'y' : 'ies'} deleted`);
      setSelectedIds(new Set());
      setDeleteAllStep(0);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entries');
    } finally {
      setBulkDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Other Cash
            </h1>
            <p className="text-slate-500 mt-1">
              Track loans, wages, rent, GST, and other cash movements
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-primary hover:bg-primary/90"
                onClick={() => handleOpenDialog()}
                data-testid="add-other-cash-btn"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingItem ? 'Edit Entry' : 'Add Cash Entry'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="cash_date">
                      {formData.is_recurring && !editingItem ? 'Next Payment Date' : 'Cash Date'}
                    </Label>
                    <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          id="cash_date"
                          type="button"
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                          data-testid="other-cash-date-input"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                          {formData.cash_date
                            ? format(parseISO(formData.cash_date), 'dd MMM yyyy')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={formData.cash_date ? parseISO(formData.cash_date) : undefined}
                          onSelect={(date) => {
                            if (date) {
                              setFormData({ ...formData, cash_date: format(date, 'yyyy-MM-dd') });
                              setDatePickerOpen(false);
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger data-testid="other-cash-category-select">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1.5 text-xs font-semibold text-green-600 bg-green-50">Cash Inflows</div>
                        {INFLOW_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              {cat}
                            </span>
                          </SelectItem>
                        ))}
                        <div className="px-2 py-1.5 text-xs font-semibold text-red-600 bg-red-50 mt-1">Cash Outflows</div>
                        {OUTFLOW_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              {cat}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="counterparty">Counterparty (optional)</Label>
                  <Input
                    id="counterparty"
                    value={formData.counterparty}
                    onChange={(e) => setFormData({ ...formData, counterparty: e.target.value })}
                    placeholder="e.g., Bank name, ATO, etc."
                    data-testid="other-cash-counterparty-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description (optional)</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Entry description"
                    data-testid="other-cash-description-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="amount">Amount (AUD)</Label>
                  <Input
                    id="amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="Enter amount (always positive)"
                    data-testid="other-cash-amount-input"
                  />
                  <p className="text-xs text-slate-500">
                    {isOutflowCategory(formData.category) ? (
                      <span className="text-red-600">This will be recorded as an outflow (cash out)</span>
                    ) : (
                      <span className="text-green-600">This will be recorded as an inflow (cash in)</span>
                    )}
                  </p>
                </div>
                
                {/* Recurrence — only show for new entries */}
                {!editingItem && (
                  <div className="rounded-lg border bg-slate-50 dark:bg-slate-800 p-4 space-y-3" data-testid="recurring-section">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label htmlFor="is_recurring" className="flex items-center gap-2">
                          <Repeat className="w-4 h-4 text-primary" />
                          Recurring entry
                        </Label>
                        <p className="text-xs text-slate-500">
                          Auto-generate this entry across the 13-month forecast.
                        </p>
                      </div>
                      <Switch
                        id="is_recurring"
                        checked={formData.is_recurring}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                        data-testid="recurring-toggle"
                      />
                    </div>

                    {formData.is_recurring && (
                      <div className="space-y-3 pt-1 border-t border-slate-200 dark:border-slate-700">
                        <Label className="text-xs uppercase tracking-wider text-slate-500 mt-2 block">Frequency</Label>
                        <div className="grid grid-cols-3 gap-2" role="radiogroup" data-testid="recurrence-frequency-group">
                          {[
                            { value: 'weekly', label: 'Weekly', sub: 'every 7 days' },
                            { value: 'fortnightly', label: 'Fortnightly', sub: 'every 14 days' },
                            { value: 'monthly', label: 'Monthly', sub: 'same day each month' },
                          ].map((opt) => {
                            const selected = formData.recurrence_frequency === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                role="radio"
                                aria-checked={selected}
                                onClick={() => setFormData({ ...formData, recurrence_frequency: opt.value })}
                                data-testid={`recurrence-${opt.value}-btn`}
                                className={`text-left rounded-md border px-3 py-2 transition-colors ${
                                  selected
                                    ? 'border-primary bg-primary/5 text-primary'
                                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                }`}
                              >
                                <p className="text-sm font-medium">{opt.label}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">{opt.sub}</p>
                              </button>
                            );
                          })}
                        </div>

                        {/* Recurrence end-date mode */}
                        <Label className="text-xs uppercase tracking-wider text-slate-500 block">Ends</Label>
                        <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid="recurrence-mode-group">
                          <button
                            type="button"
                            role="radio"
                            aria-checked={formData.recurrence_mode === 'forever'}
                            onClick={() => setFormData({ ...formData, recurrence_mode: 'forever', recurrence_end_date: '' })}
                            data-testid="recurrence-mode-forever"
                            className={`text-left rounded-md border px-3 py-2 transition-colors ${
                              formData.recurrence_mode === 'forever'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <p className="text-sm font-medium">Continuously</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">Until forecast ends</p>
                          </button>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={formData.recurrence_mode === 'until'}
                            onClick={() => setFormData({ ...formData, recurrence_mode: 'until' })}
                            data-testid="recurrence-mode-until"
                            className={`text-left rounded-md border px-3 py-2 transition-colors ${
                              formData.recurrence_mode === 'until'
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                            }`}
                          >
                            <p className="text-sm font-medium">On a specific date</p>
                            <p className="text-[11px] text-slate-500 mt-0.5">Pick an end date</p>
                          </button>
                        </div>

                        {formData.recurrence_mode === 'until' && (
                          <div className="space-y-1.5">
                            <Label className="text-xs text-slate-500">End date</Label>
                            <Popover open={endDatePickerOpen} onOpenChange={setEndDatePickerOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="w-full justify-start text-left font-normal"
                                  data-testid="recurrence-end-date-input"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                                  {formData.recurrence_end_date
                                    ? format(parseISO(formData.recurrence_end_date), 'EEE dd MMM yyyy')
                                    : 'Pick end date'}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <CalendarComponent
                                  mode="single"
                                  selected={formData.recurrence_end_date ? parseISO(formData.recurrence_end_date) : undefined}
                                  onSelect={(date) => {
                                    if (date) {
                                      setFormData({ ...formData, recurrence_end_date: format(date, 'yyyy-MM-dd') });
                                      setEndDatePickerOpen(false);
                                    }
                                  }}
                                  disabled={(d) => {
                                    if (!formData.cash_date) return false;
                                    return d < parseISO(formData.cash_date);
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}

                        <p className="text-xs text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 rounded p-2" data-testid="recurrence-preview">
                          We'll create entries{' '}
                          <strong>
                            {formData.recurrence_frequency === 'weekly' && 'every 7 days'}
                            {formData.recurrence_frequency === 'fortnightly' && 'every 14 days'}
                            {formData.recurrence_frequency === 'monthly' && 'on the same day each month'}
                          </strong>{' '}
                          starting from <strong>{formData.cash_date}</strong> until{' '}
                          <strong>
                            {formData.recurrence_mode === 'until' && formData.recurrence_end_date
                              ? format(parseISO(formData.recurrence_end_date), 'dd MMM yyyy')
                              : formData.recurrence_mode === 'until'
                                ? '— pick an end date —'
                                : 'the end of the 13-month forecast window'}
                          </strong>.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" data-testid="cancel-other-cash-btn">Cancel</Button>
                </DialogClose>
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="save-other-cash-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Category Legend */}
        <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Category Types</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="flex items-center gap-2 font-medium text-green-700 mb-2">
                <span className="w-3 h-3 bg-green-500 rounded-full"></span>
                Cash Inflows
              </span>
              <p className="text-xs text-slate-500 ml-5">{INFLOW_CATEGORIES.join(', ')}</p>
            </div>
            <div>
              <span className="flex items-center gap-2 font-medium text-red-700 mb-2">
                <span className="w-3 h-3 bg-red-500 rounded-full"></span>
                Cash Outflows
              </span>
              <p className="text-xs text-slate-500 ml-5">{OUTFLOW_CATEGORIES.join(', ')}</p>
            </div>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {items.length === 0 ? (
              <div className="empty-state py-16">
                <Wallet className="empty-state-icon w-16 h-16 text-slate-300 mb-4" />
                <h3 className="empty-state-title text-lg font-semibold text-slate-900 mb-2">No cash entries yet</h3>
                <p className="empty-state-text text-sm text-slate-500 max-w-md">
                  Add other cash movements like loans, wages, rent, and GST payments.
                </p>
              </div>
            ) : (
              <>
                <div className="p-4 border-b space-y-3">
                  <div className="flex flex-wrap items-center gap-3">
                    <Input
                      type="search"
                      placeholder="Search by category, counterparty, description, or amount..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="max-w-md"
                      data-testid="other-cash-search-input"
                    />
                    <div className="flex items-center gap-2">
                      <Label className="text-xs text-slate-500 whitespace-nowrap">From</Label>
                      <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="h-9 w-36"
                        data-testid="other-cash-date-from"
                      />
                      <Label className="text-xs text-slate-500 whitespace-nowrap">To</Label>
                      <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="h-9 w-36"
                        data-testid="other-cash-date-to"
                      />
                      {(dateFrom || dateTo) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearDateRange}
                          className="h-8 px-2 text-slate-500"
                          data-testid="other-cash-clear-range-btn"
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                    <div
                      className="inline-flex items-center gap-3 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 dark:bg-slate-800 dark:border-slate-700"
                      data-testid="other-cash-summary-panel"
                    >
                      <span className="text-xs text-slate-500 uppercase tracking-wider">
                        {(dateFrom || dateTo) ? 'In Range' : 'All'}
                      </span>
                      <span className="text-sm font-medium text-slate-900 dark:text-slate-100" data-testid="other-cash-summary-count">
                        {filteredItems.length} entr{filteredItems.length === 1 ? 'y' : 'ies'}
                      </span>
                      <span className="text-slate-300">·</span>
                      <span
                        className={`text-sm font-mono font-semibold ${filteredTotal < 0 ? 'text-red-700' : 'text-emerald-700'}`}
                        data-testid="other-cash-summary-total"
                      >
                        {formatCurrency(filteredTotal)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedIds.size > 0 && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={bulkDeleting}
                              data-testid="other-cash-delete-selected-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1.5" />
                              Delete Selected ({selectedIds.size})
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete {selectedIds.size} entr{selectedIds.size === 1 ? 'y' : 'ies'}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the selected entries. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={handleBulkDeleteSelected}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="other-cash-confirm-delete-selected"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-700 border-red-200 hover:bg-red-50"
                        disabled={filteredItems.length === 0 || bulkDeleting}
                        onClick={() => setDeleteAllStep(1)}
                        data-testid="other-cash-delete-all-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Delete All{(dateFrom || dateTo || searchQuery) ? ' Filtered' : ''} ({filteredItems.length})
                      </Button>
                    </div>
                  </div>
                </div>
              <div className="overflow-x-auto">
                <Table data-testid="other-cash-table">
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-10">
                        <Checkbox
                          checked={allFilteredSelected}
                          onCheckedChange={toggleSelectAll}
                          disabled={filteredItems.length === 0}
                          aria-label="Select all"
                          data-testid="other-cash-select-all"
                        />
                      </TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Date</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Category</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Counterparty</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right">Amount</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500">Month</TableHead>
                      <TableHead className="font-semibold text-xs uppercase tracking-wider text-slate-500 text-right w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item) => (
                      <TableRow key={item.id} data-testid={`other-cash-row-${item.id}`}>
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(item.id)}
                            onCheckedChange={() => toggleRow(item.id)}
                            aria-label="Select row"
                            data-testid={`other-cash-select-${item.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {format(parseISO(item.cash_date), 'dd MMM yyyy')}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                            {item.category}
                          </span>
                        </TableCell>
                        <TableCell className="text-slate-600">{item.counterparty || '-'}</TableCell>
                        <TableCell className="text-slate-600 max-w-xs truncate">{item.description || '-'}</TableCell>
                        <TableCell className={`text-right font-mono text-sm font-medium ${getCashClass(item.amount)}`}>
                          {formatCurrency(item.amount)}
                        </TableCell>
                        <TableCell className="text-sm text-slate-500">
                          {format(parseISO(item.cash_month), 'MMM yyyy')}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openShiftDialog(item)}
                              data-testid={`shift-other-cash-${item.id}`}
                              title="Shift date"
                            >
                              <ChevronsRight className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenDialog(item)}
                              data-testid={`edit-other-cash-${item.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-other-cash-${item.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Entry</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete this entry? This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDelete(item.id)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete-All double confirmation */}
      <AlertDialog
        open={deleteAllStep === 1}
        onOpenChange={(open) => { if (!open) setDeleteAllStep(0); }}
      >
        <AlertDialogContent data-testid="other-cash-delete-all-step1">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {filteredItems.length} entr{filteredItems.length === 1 ? 'y' : 'ies'}?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {(dateFrom || dateTo || searchQuery) ? 'all currently filtered entries' : 'every entry'} (net {formatCurrency(filteredTotal)})?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="other-cash-delete-all-step1-cancel">No</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); setDeleteAllStep(2); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="other-cash-delete-all-step1-yes"
            >
              Yes, continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteAllStep === 2}
        onOpenChange={(open) => { if (!open) setDeleteAllStep(0); }}
      >
        <AlertDialogContent data-testid="other-cash-delete-all-step2">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">This cannot be undone</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to permanently delete {filteredItems.length} entr{filteredItems.length === 1 ? 'y' : 'ies'} (net {formatCurrency(filteredTotal)}).
              This action is irreversible. Are you absolutely sure?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="other-cash-delete-all-step2-cancel">No, keep them</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteAllFiltered}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="other-cash-delete-all-step2-yes"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Yes, delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Shift Date Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent className="max-w-md" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Shift Cash Date
            </DialogTitle>
          </DialogHeader>
          {shiftItem && (
            <div className="space-y-5 py-2">
              {/* Current entry summary */}
              <div className="rounded-md border bg-slate-50 dark:bg-slate-800 p-3 text-sm space-y-0.5">
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  {shiftItem.category}
                  {shiftItem.counterparty ? ` · ${shiftItem.counterparty}` : ''}
                </p>
                <p className="text-xs text-slate-500">
                  Currently scheduled for{' '}
                  <span className="font-mono text-slate-700 dark:text-slate-200">
                    {format(parseISO(shiftItem.cash_date), 'EEE dd MMM yyyy')}
                  </span>
                  {' · '}
                  <span className={shiftItem.amount < 0 ? 'text-red-600' : 'text-emerald-600'}>
                    {formatCurrency(shiftItem.amount)}
                  </span>
                </p>
              </div>

              {/* Direction */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500">Direction</Label>
                <div className="grid grid-cols-2 gap-2" role="radiogroup">
                  <button
                    type="button"
                    onClick={() => setShiftDirection('earlier')}
                    data-testid="shift-earlier-btn"
                    className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                      shiftDirection === 'earlier'
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <ArrowLeft className="w-4 h-4" />
                      Bring forward
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">Receive sooner</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setShiftDirection('later')}
                    data-testid="shift-later-btn"
                    className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                      shiftDirection === 'later'
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 font-medium text-sm">
                      <ArrowRight className="w-4 h-4" />
                      Push later
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5">Delay payment</p>
                  </button>
                </div>
              </div>

              {/* Days */}
              <div className="space-y-2">
                <Label htmlFor="shift-days" className="text-xs uppercase tracking-wider text-slate-500">
                  Days
                </Label>
                <Input
                  id="shift-days"
                  type="number"
                  min="1"
                  value={shiftDays}
                  onChange={(e) => setShiftDays(e.target.value)}
                  data-testid="shift-days-input"
                />
                <p className="text-xs text-slate-500">
                  New date:{' '}
                  <span className="font-mono text-slate-700 dark:text-slate-200">
                    {(() => {
                      const days = Math.abs(parseInt(shiftDays, 10) || 0);
                      if (!days) return '—';
                      const base = parseISO(shiftItem.cash_date);
                      const nd = new Date(base);
                      nd.setDate(nd.getDate() + (shiftDirection === 'later' ? days : -days));
                      return format(nd, 'EEE dd MMM yyyy');
                    })()}
                  </span>
                </p>
              </div>

              {/* Scope (only for recurring) */}
              {shiftItem.recurring_group_id && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Apply to</Label>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup">
                    <button
                      type="button"
                      onClick={() => setShiftScope('this')}
                      data-testid="shift-scope-this-btn"
                      className={`rounded-md border px-3 py-2 text-left transition-colors ${
                        shiftScope === 'this'
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <p className="text-sm font-medium">Just this entry</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => setShiftScope('future')}
                      data-testid="shift-scope-future-btn"
                      className={`rounded-md border px-3 py-2 text-left transition-colors ${
                        shiftScope === 'future'
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                      }`}
                    >
                      <p className="text-sm font-medium">This + future</p>
                      <p className="text-[11px] text-slate-500">Same recurring series</p>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShiftDialogOpen(false)} data-testid="cancel-shift-btn">
              Cancel
            </Button>
            <Button
              onClick={handleShift}
              disabled={shifting}
              className="bg-primary hover:bg-primary/90"
              data-testid="confirm-shift-btn"
            >
              {shifting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply shift
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
